import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import useHover from 'react-use-hover';
import { getProfileName } from '~/BonjorosList/util';
import React, { useContext, useMemo, useEffect } from 'react';

import Compact from './Compact';
import colours from '~/utils/colours';
import ProfileImage from '~/App/Components/ProfileImage';
import { useRelativeDate } from '~/react-hooks/use-date';

const IMAGE_SIZE_PX = 55;

const Container = styled.div`
  padding: 10px 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  padding: 0 10px;
`;

const TextHeaderContainer = styled.div`
  display: flex;
  margin-left: 15px;
  align-items: left;
  flex-direction: column;
  justify-content: center;
`;

const Name = styled.h3`
  padding: 0;
  margin: 0;
`;

const CompanyName = styled(Name)`
  color: ${colours.grey};
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85em;
  padding: 0 10px;
`;

const FieldName = styled.span`
  color: ${colours.grey};
  margin-right: 5px;
  user-select: none;
`;

const FieldValue = styled.span``;

const Field = styled.div`
  &:not(last-child) {
    margin-right: 15px;
  }
`;

const ProfileContext = React.createContext({
  profile: null,
  onHover: null,
});

function ProfilePopover() {
  const { profile, onHover } = useContext(ProfileContext);
  const firstCreated = useRelativeDate(profile.created_at);
  const [hovered, hoverProps] = useHover({
    mouseEnterDelayMS: 0,
    mouseLeaveDelayMS: 0,
  });

  useEffect(() => {
    if (typeof onHover === 'function') {
      onHover(hovered);
    }
  }, [hovered]);

  return (
    <Container {...hoverProps}>
      <HeaderContainer>
        <ProfileImage profile={profile} size={IMAGE_SIZE_PX} popover={false} />
        <TextHeaderContainer>
          <Name>{getProfileName(profile)}</Name>
          <CompanyName as='h5'>{profile.work.current}</CompanyName>
        </TextHeaderContainer>
      </HeaderContainer>
      <hr />
      <DetailsContainer>
        <Field>
          <FieldName>Email</FieldName>
          <FieldValue>{profile.email}</FieldValue>
        </Field>
        <Field>
          <FieldName>First Created</FieldName>
          <FieldValue>{firstCreated}</FieldValue>
        </Field>
      </DetailsContainer>
    </Container>
  );
}

ProfilePopover.propTypes = {
  openPopover: PropTypes.func.isRequired,
  closePopover: PropTypes.func.isRequired,
  togglePopover: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  profile: PropTypes.object,
};

export default function Profile({ children, profile, onHover }) {
  return (
    <ProfileContext.Provider value={{ profile, onHover }}>
      <Compact
        popoverBody={ProfilePopover}
        enterExitTransitionDurationMs={250}
        align='left'
      >
        {children}
      </Compact>
    </ProfileContext.Provider>
  );
}

Profile.propTypes = {
  children: PropTypes.any,
  profile: PropTypes.object.isRequired,
  onHover: PropTypes.func,
};
